import { openDB, type IDBPDatabase } from 'idb';
import type { FormaPagamentoPdv } from '@/features/pdv/types/pdv.types';

// ── Schema ────────────────────────────────────────────────────────────────────

export interface ItemOffline {
  produtoId: number;
  quantidade: number;
  precoUnitario: number;
  descricao: string;
  subProdutoId?: number;
}

export interface PagamentoOffline {
  formaPagamento: FormaPagamentoPdv;
  valor: number;
  parcelas?: number;
}

export type StatusVendaOffline = 'Pendente' | 'Sincronizando' | 'Sincronizada' | 'ConflitoPendente' | 'Descartada';

export interface VendaPendente {
  idLocal: string;          // uuid gerado no frontend
  empresaId: number;
  caixaId: number;
  clienteId?: number;
  clienteNome?: string;
  itens: ItemOffline[];
  pagamentos: PagamentoOffline[];
  totalLiquido: number;
  dtCriacao: string;        // ISO
  tentativas: number;
  status: StatusVendaOffline;
  erroSync?: string;
}

export interface ProdutoCache {
  id: number;
  dados: {
    id: number;
    descricao: string;
    codigoBarras?: string;
    codigoInterno?: string;
    precoUnitario: number;
    estoqueAtual: number;
    permiteFracionado: boolean;
  };
  dtCache: string;
}

export interface ClienteCache {
  id: number;
  dados: {
    id: number;
    nome: string;
    cpfCnpj?: string;
    telefone?: string;
  };
  dtCache: string;
}

export interface ConfigPdvCache {
  chave: string;
  valor: unknown;
  dtCache: string;
}

// ── DB ────────────────────────────────────────────────────────────────────────

const DB_NAME    = 'udisys-pdv';
const DB_VERSION = 1;

let _db: IDBPDatabase | null = null;

async function getDb(): Promise<IDBPDatabase> {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('vendas_pendentes')) {
        const vs = db.createObjectStore('vendas_pendentes', { keyPath: 'idLocal' });
        vs.createIndex('status', 'status');
        vs.createIndex('dtCriacao', 'dtCriacao');
      }
      if (!db.objectStoreNames.contains('produtos_cache')) {
        db.createObjectStore('produtos_cache', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('clientes_cache')) {
        db.createObjectStore('clientes_cache', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('config_pdv_cache')) {
        db.createObjectStore('config_pdv_cache', { keyPath: 'chave' });
      }
    },
  });
  return _db;
}

// ── Vendas Pendentes ──────────────────────────────────────────────────────────

export const offlineDb = {
  async salvarVendaPendente(venda: VendaPendente): Promise<void> {
    const db = await getDb();
    await db.put('vendas_pendentes', venda);
  },

  async obterVendasPendentes(): Promise<VendaPendente[]> {
    const db = await getDb();
    return db.getAll('vendas_pendentes');
  },

  async obterVendaPendente(idLocal: string): Promise<VendaPendente | undefined> {
    const db = await getDb();
    return db.get('vendas_pendentes', idLocal);
  },

  async atualizarStatusVenda(idLocal: string, status: StatusVendaOffline, erro?: string): Promise<void> {
    const db   = await getDb();
    const venda = await db.get('vendas_pendentes', idLocal);
    if (!venda) return;
    venda.status    = status;
    venda.erroSync  = erro;
    venda.tentativas = (venda.tentativas ?? 0) + (status === 'Sincronizando' ? 1 : 0);
    await db.put('vendas_pendentes', venda);
  },

  async removerVendaPendente(idLocal: string): Promise<void> {
    const db = await getDb();
    await db.delete('vendas_pendentes', idLocal);
  },

  async contarPendentes(): Promise<number> {
    const db    = await getDb();
    const todas = await db.getAll('vendas_pendentes');
    return todas.filter(v => v.status === 'Pendente' || v.status === 'Sincronizando').length;
  },

  async contarConflitos(): Promise<number> {
    const db    = await getDb();
    const todas = await db.getAll('vendas_pendentes');
    return todas.filter(v => v.status === 'ConflitoPendente').length;
  },

  // ── Produtos Cache ──────────────────────────────────────────────────────────

  async salvarProdutosCache(produtos: ProdutoCache[]): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('produtos_cache', 'readwrite');
    await Promise.all(produtos.map(p => tx.store.put(p)));
    await tx.done;
  },

  async buscarProdutoCache(termo: string): Promise<ProdutoCache['dados'][]> {
    const db    = await getDb();
    const todos = await db.getAll('produtos_cache');
    const t     = termo.toLowerCase();
    return todos
      .filter(p =>
        p.dados.descricao.toLowerCase().includes(t) ||
        p.dados.codigoBarras?.includes(t) ||
        p.dados.codigoInterno?.includes(t)
      )
      .map(p => p.dados)
      .slice(0, 10);
  },

  // ── Clientes Cache ──────────────────────────────────────────────────────────

  async salvarClientesCache(clientes: ClienteCache[]): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('clientes_cache', 'readwrite');
    await Promise.all(clientes.map(c => tx.store.put(c)));
    await tx.done;
  },

  async buscarClienteCache(termo: string): Promise<ClienteCache['dados'][]> {
    const db    = await getDb();
    const todos = await db.getAll('clientes_cache');
    const t     = termo.toLowerCase();
    return todos
      .filter(c =>
        c.dados.nome.toLowerCase().includes(t) ||
        c.dados.cpfCnpj?.includes(t) ||
        c.dados.telefone?.includes(t)
      )
      .map(c => c.dados)
      .slice(0, 10);
  },

  // ── Config Cache ────────────────────────────────────────────────────────────

  async salvarConfig(chave: string, valor: unknown): Promise<void> {
    const db = await getDb();
    await db.put('config_pdv_cache', { chave, valor, dtCache: new Date().toISOString() });
  },

  async obterConfig<T>(chave: string): Promise<T | undefined> {
    const db  = await getDb();
    const rec = await db.get('config_pdv_cache', chave);
    return rec?.valor as T | undefined;
  },

  async limparCache(): Promise<void> {
    const db = await getDb();
    await db.clear('produtos_cache');
    await db.clear('clientes_cache');
  },
};
